import type { Move } from '../../../schemas/move';
import { paladinoArchetypeMoves } from './archetypes';
import {
  paladinoInitialMoves,
  paladinoStartingAutoMoves,
  paladinoStartingChoiceMoves,
} from './initial';
import { paladinoAdvanced2to5Moves } from './advanced-2-5';
import { paladinoAdvanced6to10Moves } from './advanced-6-10';

export {
  paladinoArchetypeMoves,
  paladinoInitialMoves,
  paladinoStartingAutoMoves,
  paladinoStartingChoiceMoves,
  paladinoAdvanced2to5Moves,
  paladinoAdvanced6to10Moves,
};

/**
 * Catalogo completo delle mosse del Paladino (manuale V1, sezione "Il
 * Paladino", rr. 4780-5128).
 *
 * Aggregato di:
 *  - 5 archetipi (`class_special`)
 *  - 4 mosse iniziali automatiche + 2 mosse iniziali di scelta (`starting`,
 *    `pickCount: 1` sul gruppo di scelta — *Ordine Sacro* / *Ordine
 *    Sovrannaturale*)
 *  - 16 mosse avanzate L2-5 (`advanced_2_5`)
 *  - 14 mosse avanzate L6-10 (`advanced_6_10`)
 *
 * Totale: 41 mosse.
 *
 * **Pattern stress-testati**:
 *  - **FI-1 chiuso strutturalmente**: prima applicazione di
 *    `Archetype.baseHpOverride` (Paladino-Nano: 12 + COS).
 *  - **Casteggiante condizionato a 2 livelli**: *Ordine Sacro* attiva
 *    `paladino-native` con Tradizione (Negromanzia/Valore/Natura);
 *    *Ordine Sovrannaturale* è description-only stub (FI-27, refactor
 *    nella PR Warlock).
 *  - **`MoveSchema.replaces` 3 casi consecutivi nella stessa classe**:
 *    *Attacco Coordinato* sostituisce *Attacco a Seguire*; *Difensore
 *    Inattaccabile* sostituisce *Difensore Devoto*; *Sempre Avanti!*
 *    sostituisce *Carica!*.
 *  - **Cross-class forward-refs** in `prerequisites.anyOf`: *Io Sono la
 *    Legge*, *Missione*, *Aura*, *Imposizione delle Mani* citano
 *    `chierico.divinita`, `druido.divinita-della-natura`,
 *    `warlock.contratto`. Per il Paladino L1 il prereq è soddisfatto
 *    automaticamente da *Faro di Devozione* (auto). I forward-refs cross-class
 *    sono accettati come `knownExternalIds` nei test di integrità.
 *  - **Mossa-config *Faro di Devozione*** con 7 Aspetti in `MoveSchema.choices`
 *    (`choiceCount: 1`); Fondamento + nome entità in description (FI-18).
 *  - **Mossa-config *Aura*** con 5 scopi in `MoveSchema.choices`.
 *  - **Mossa-config *Ordine Sacro*** con 3 Tradizioni in `MoveSchema.choices`
 *    (Natura richiede archetipo Elfo via `SpellAccess.requiresArchetype`,
 *    FI-2 risolto).
 *  - **`prerequisites` composto**: *Maestro Ospitaliere* richiede
 *    *Ospitaliere* + (Aura O Imposizione delle Mani) — pattern
 *    `[stringa, {anyOf: [...]}]`.
 *  - **Mosse multiclasse condizionate dall'Ordine** (*Crociato*, *Templare*):
 *    description-only (FI-17).
 *  - **FI-8 stub**: *Richiama Cavalcatura*, *Comandante Supremo*, *Nato in
 *    Sella*, *La Voce dell'Autorità* sono description-only.
 */
export const paladinoMoves: readonly Move[] = [
  ...paladinoArchetypeMoves,
  ...paladinoInitialMoves,
  ...paladinoAdvanced2to5Moves,
  ...paladinoAdvanced6to10Moves,
];
